import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './VendorDetails.css';
import './FoodVendors.css';
import FoodNavbar from '../navbar/FoodNavbar';
import { calculateDeliveryTime, getClosingTime } from './vendor';
import VendorMoreInfoModal from './VendorMoreInfoModal';
import ProductDetailsModal from './ProductDetailsModal';
import { useCart } from './CartContext';
import { useAuth } from '../../AuthContext';
import VendorCheckout from './VendorCheckout';
import { db } from '../../firebase';
import { ref, update, remove } from "firebase/database";

function StoreDetails() {
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const auth = useAuth();
  const navigate = useNavigate();
  const [isMoreInfoModalOpen, setIsMoreInfoModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const { addToCart, cartItemCount } = useCart();
  const { vendor, address, locationType } = location.state || {};

  const handleGoToCheckout = () => {
    sessionStorage.setItem('9troride_checkoutVendorId', vendor.id);
    sessionStorage.setItem('9troride_checkoutVendorType', 'store');
    navigate('/checkout', { state: { vendorId: vendor.id, vendorName: vendor.name, vendorImage: vendor.imageUrl, vendorType: 'store' } });
  };

  const handleFavoriteClick = () => {
    if (!auth.currentUser) {
      navigate('/login', { state: { from: location } });
      return;
    }

    const userId = auth.currentUser.uid;
    const isFavorite = auth.riderData?.favorites?.[vendor.id];

    if (isFavorite) {
      // Remove from favorites
      remove(ref(db, `favorites/${userId}/${vendor.id}`));
    } else {
      // Add to favorites
      update(ref(db, `favorites/${userId}`), { [vendor.id]: { type: 'store' } });
    }
  };

  if (!vendor) {
    return (
      <div className="vendor-details-page empty-state">
        <h2>Store details not found.</h2>
        <button onClick={() => navigate(-1)} className="details-back-btn">Go Back</button>
      </div>
    );
  }

  const delivery = calculateDeliveryTime(location.state.selectedLocation, vendor?.location);

  const filteredProducts = (vendor.fullProducts || []).filter(product => {
    const cat = String(product.itemCategory || "").toLowerCase();
    const matchesSearch = String(product.name || "").toLowerCase().includes(searchQuery.toLowerCase().trim());
    
    return (cat === 'item' || cat === 'beverage') && matchesSearch;
  });

  return (
    <div className="vendor-details-page">
      <FoodNavbar 
        showBackBtn={true}
        searchPlaceholder={`Search in ${vendor.name}...`}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        address={address}
        locationType={locationType}
        cartItemCount={cartItemCount}
      />

      <div className="vendor-details-content has-sidebar">
        <div className="vendor-details-sidebar">
           <div className="vendor-banner-wrapper" style={{ borderRadius: '12px', overflow: 'hidden', height: '160px', position: 'relative' }}>
             <div className="vendor-banner" style={{ backgroundImage: `url('${vendor.bannerUrl || vendor.imageUrl}')`, height: '100%' }}></div>
             <button className={`vendor-card-fav ${auth.riderData?.favorites?.[vendor.id] ? 'is-favorite' : ''}`} onClick={handleFavoriteClick}>
               <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
             </button>
             <div className="vendor-delivery-badge" style={{ color: delivery.isFast ? '#00b84c' : '#111' }}>
               <span>{delivery.text}</span>
             </div>
             <img src={vendor.imageUrl} alt={vendor.name} className="vendor-details-profile-img" style={{ position: 'absolute', bottom: '16px', left: '16px', zIndex: 2, border: '4px solid #fff', backgroundColor: '#fff', width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover' }} />
           </div>
           <div className="vendor-details-title-row">
             <h1>{vendor.name}</h1>
             <div className="vendor-verified-badge" title="Verified">
               <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                   <path d="M12 1L14.75 3.06L18.11 2.72L19.31 5.89L22.3 7.15L21.1 10.32L23 13.06L20.52 15.42L20.49 18.78L17.2 19.34L15.4 22.1L12 21.05L8.6 22.1L6.8 19.34L3.51 18.78L3.48 15.42L1 13.06L2.9 10.32L1.7 7.15L4.69 5.89L5.89 2.72L9.25 3.06L12 1Z" fill="currentColor"/>
                   <path d="M10.5 16L6.5 12L7.91 10.59L10.5 13.17L16.09 7.59L17.5 9L10.5 16Z" fill="white"/>
               </svg>
             </div>
           </div>

           <div className="vendor-details-meta">
              <div className="meta-stats-row">
                <div className="meta-stat">
                  <div className="meta-stat-value">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#E3242B'}}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
                    <span>{vendor.rating ? vendor.rating.toFixed(1) : 'New'}</span>
                  </div>
                  <span className="meta-stat-label">{vendor.reviewsCount ? `${vendor.reviewsCount}+ ratings` : 'No ratings'}</span>
                </div>
                <div className="meta-divider"></div>
                <div className="meta-stat">
                  <div className="meta-stat-value">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                    <span>{getClosingTime(vendor.workingHours)}</span>
                  </div>
                  <span className="meta-stat-label">Closing time</span>
                </div>
              </div>
              <button className="details-more-info-btn" onClick={() => setIsMoreInfoModalOpen(true)}>
                <span>More Info</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
              </button>
           </div>
        </div>
        
        <hr />

        <div className="vendor-menu-section">
           {filteredProducts.length > 0 ? (
             <div className="menu-grid">
               {filteredProducts.map((product) => (
                 <div key={product.id} className="menu-item-card" onClick={() => setSelectedProduct(product)}>
                   <div className="menu-item-info">
                     <h3>{product.name}</h3>
                     {product.description && <p className="menu-item-desc">{product.description}</p>}
                     <span className="menu-item-price">{product.price}</span>
                     {!product.image && <button className="add-to-cart-btn-no-img">+</button>}
                   </div>
                   {product.image && (
                     <div className="menu-item-image-wrapper">
                       <img src={product.image} alt={product.name} className="menu-item-image" />
                       <button className="add-to-cart-btn">+</button>
                     </div>
                   )}
                 </div>
               ))}
             </div>
           ) : (
             <p className="empty-menu">
               {searchQuery ? `No items match "${searchQuery}".` : "No items listed yet."}
             </p>
           )}
        </div>

        <VendorCheckout vendorId={vendor.id} onCheckout={handleGoToCheckout} />
      </div>

      {isMoreInfoModalOpen && (
        <VendorMoreInfoModal 
          vendor={vendor} 
          vendorType="store" 
          onClose={() => setIsMoreInfoModalOpen(false)} 
        />
      )}

      {selectedProduct && (
        <ProductDetailsModal 
          product={selectedProduct} 
          onClose={() => setSelectedProduct(null)} 
          onAddToCart={(item) => addToCart({ ...item, vendor, vendorType: 'store', address, locationType })}
        />
      )}
    </div>
  );
}

export default StoreDetails;